/**
 * Help & Feedback drawer open-state, lifted into a tiny context.
 *
 * The drawer itself (`HelpFeedbackDrawer`) and its open-state used to live
 * entirely inside `Layout`, reachable only via the sidebar's `onOpenFeedback`
 * prop. The Settings page (rendered through the router's `<Outlet>`, a
 * descendant of `Layout`) needs to open the same drawer from an inline link, so
 * the open-state is exposed through this context. The provider is mounted in
 * `Layout`, above the `<Outlet>`, so both the sidebar and any routed page can
 * call `openFeedback()`. Matches the `ToastProvider` pattern (a small context
 * with a throwing `use*` hook).
 */

import * as React from "react";

interface HelpFeedbackContextValue {
  /** Open the Help & Feedback drawer. */
  openFeedback: () => void;
}

const HelpFeedbackContext = React.createContext<HelpFeedbackContextValue | null>(null);

export function useHelpFeedback(): HelpFeedbackContextValue {
  const ctx = React.useContext(HelpFeedbackContext);
  if (!ctx) {
    throw new Error("useHelpFeedback must be used within a <HelpFeedbackProvider>");
  }
  return ctx;
}

export function HelpFeedbackProvider({
  openFeedback,
  children,
}: {
  openFeedback: () => void;
  children: React.ReactNode;
}) {
  const value = React.useMemo(() => ({ openFeedback }), [openFeedback]);
  return <HelpFeedbackContext.Provider value={value}>{children}</HelpFeedbackContext.Provider>;
}
